import React from 'react'
import { Link, Outlet, NavLink, useLocation } from 'react-router-dom'
import DocsSubHeader from './DocsSubHeader'
import ThemeToggle from './ThemeToggle'
import { useTheme } from '../context/ThemeContext'
import { useCatalog } from '../context/CatalogContext'

export default function Layout() {
  const location = useLocation()
  const softwareDropdownRef = React.useRef<HTMLDetailsElement | null>(null)
  const devDropdownRef = React.useRef<HTMLDetailsElement | null>(null)
  const { theme } = useTheme()
  const { catalog } = useCatalog()

  // Close dropdowns whenever the route changes
  React.useEffect(() => {
    if (softwareDropdownRef.current?.hasAttribute('open')) {
      softwareDropdownRef.current.removeAttribute('open')
    }
    if (devDropdownRef.current?.hasAttribute('open')) {
      devDropdownRef.current.removeAttribute('open')
    }
  }, [location.pathname])

  // Utility: close all dropdowns
  const closeDropdown = React.useCallback(() => {
    if (softwareDropdownRef.current?.hasAttribute('open')) {
      softwareDropdownRef.current.removeAttribute('open')
    }
    if (devDropdownRef.current?.hasAttribute('open')) {
      devDropdownRef.current.removeAttribute('open')
    }
  }, [])

  // Ensure only a single dropdown is open at any time
  const handleSoftwareToggle = (e: React.SyntheticEvent<HTMLDetailsElement>) => {
    const el = e.currentTarget
    if ((el as HTMLDetailsElement).open) {
      devDropdownRef.current?.removeAttribute('open')
    }
  }
  const handleDevToggle = (e: React.SyntheticEvent<HTMLDetailsElement>) => {
    const el = e.currentTarget
    if ((el as HTMLDetailsElement).open) {
      softwareDropdownRef.current?.removeAttribute('open')
    }
  }

  // Close dropdowns on the outside click and on Escape
  React.useEffect(() => {
    const handlePointerDown = (evt: MouseEvent | PointerEvent) => {
      const t = evt.target as Node
      const softEl = softwareDropdownRef.current
      const devEl = devDropdownRef.current
      // If the click is inside any dropdown, ignore; otherwise close all.
      if ((softEl && softEl.contains(t)) || (devEl && devEl.contains(t))) {
        return
      }
      closeDropdown()
    }
    const handleKeyDown = (evt: KeyboardEvent) => {
      if (evt.key === 'Escape') {
        closeDropdown()
      }
    }
    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [closeDropdown])

  return (
    <div className="app-root">
      <header className="site-header">
        <div className="header-inner">
          <Link to="/" className="brand">
            <img
              className="brand-logo"
              src={theme === 'dark' ? '/assets/brand/logo-white.svg' : '/assets/brand/logo-forrest.svg'}
              alt="NordicScreen"
            />
            <span className="brand-name">Developers</span>
          </Link>
          <nav className="main-nav">
            <ThemeToggle/>
            <NavLink to="/" end className={({ isActive }) => (isActive ? 'active' : '')}>
              Home
            </NavLink>
            {/* Software dropdown */}
            <details ref={softwareDropdownRef} className="nav-dropdown" onToggle={handleSoftwareToggle}>
              <summary
                aria-haspopup="menu"
                aria-label="Software menu"
                className={location.pathname.startsWith('/software') ? 'active' : undefined}
              >
                <span className="nav-label">Software</span>
              </summary>
              <div className="dropdown-menu" role="menu" onClickCapture={closeDropdown}>
                <NavLink to="/software" end className={({ isActive }) => (isActive ? 'active' : '')} role="menuitem">
                  Software
                </NavLink>

                <div className="menu-section" role="none"
                     style={{ borderTop: '1px solid var(--border)', marginTop: 6, paddingTop: 6 }}>
                  <div className="menu-section-label" role="none"
                       style={{ fontSize: 12, opacity: 0.7, padding: '4px 2px' }}>
                    [Q-Play]
                  </div>
                  <NavLink to="/software/player-software" className={({ isActive }) => (isActive ? 'active' : '')}
                           role="menuitem">
                    Player software
                  </NavLink>
                  <NavLink to="/software/content-cache-proxy" className={({ isActive }) => (isActive ? 'active' : '')}
                           role="menuitem">
                    Content Cache Proxy
                  </NavLink>
                  <NavLink to="/software/integration-server" className={({ isActive }) => (isActive ? 'active' : '')}
                           role="menuitem">
                    Integration Server
                  </NavLink>
                </div>
              </div>
            </details>
            {/* Documentation dropdown (renamed from Development) */}
            <details ref={devDropdownRef} className="nav-dropdown" onToggle={handleDevToggle}>
              <summary
                aria-haspopup="menu"
                aria-label="Documentation menu"
                className={
                  location.pathname.startsWith('/docs')
                    ? 'active'
                    : undefined
                }
              >
                <span className="nav-label">Documentation</span>
              </summary>
              <div className="dropdown-menu" role="menu" onClickCapture={closeDropdown}>
                {/* Getting started (points to Docs index) */}
                <NavLink to="/docs" className={({ isActive }) => (isActive ? 'active' : '')} role="menuitem">
                  Getting started
                </NavLink>

                {/* Resources section */}
                <div className="menu-section" role="none"
                     style={{ borderTop: '1px solid var(--border)', marginTop: 6, paddingTop: 6 }}>
                  <div className="menu-section-label" role="none"
                       style={{ fontSize: 12, opacity: 0.7, padding: '4px 2px' }}>
                    Resources
                  </div>
                  {(() => {
                    const resourceDocs = catalog?.docs.filter(d => d.section === 'resources' || d.section === 'external') || []
                    const functionsApi = catalog?.apis.find(a => a.id === 'qplay-integration-functions')
                    let functionsRendered = false

                    const elements = resourceDocs.map(doc => {
                      const isAppIntegration = doc.slug === 'qplay-wiki' || doc.path === '/docs/app-integration-development'
                      const navLink = (
                        <NavLink
                          key={doc.slug}
                          to={doc.section === 'external' ? doc.path : `/docs/${doc.slug}`}
                          className={({ isActive }) => (isActive ? 'active' : '')}
                          role="menuitem"
                        >
                          {doc.title}
                        </NavLink>
                      )

                      if (isAppIntegration && functionsApi) {
                        functionsRendered = true
                        return (
                          <React.Fragment key={doc.slug}>
                            {navLink}
                            <NavLink
                              key={functionsApi.id}
                              to={`/docs/api-docs/${functionsApi.id}`}
                              className={({ isActive }) => (isActive ? 'active' : '')}
                              role="menuitem"
                            >
                              {functionsApi.title}
                            </NavLink>
                          </React.Fragment>
                        )
                      }
                      return navLink
                    })

                    if (functionsApi && !functionsRendered) {
                      elements.unshift(
                        <NavLink
                          key={functionsApi.id}
                          to={`/docs/api-docs/${functionsApi.id}`}
                          className={({ isActive }) => (isActive ? 'active' : '')}
                          role="menuitem"
                        >
                          {functionsApi.title}
                        </NavLink>
                      )
                    }

                    return elements
                  })()}
                </div>

                {/* API Specifications section */}
                <div className="menu-section" role="none"
                     style={{ borderTop: '1px solid var(--border)', marginTop: 6, paddingTop: 6 }}>
                  <div className="menu-section-label" role="none"
                       style={{ fontSize: 12, opacity: 0.7, padding: '4px 2px' }}>
                    API Specifications
                  </div>
                  {catalog?.apis.filter(api => api.id !== 'qplay-integration-functions').map(api => (
                    <NavLink
                      key={api.id}
                      to={`/docs/api-docs/${api.id}`}
                      className={({ isActive }) => (isActive ? 'active' : '')}
                      role="menuitem"
                    >
                      {api.title}
                    </NavLink>
                  ))}
                </div>
              </div>
            </details>
            <NavLink to="/support" className={({ isActive }) => (isActive ? 'active' : '')}>
              Support
            </NavLink>
          </nav>
        </div>
      </header>
      {/* Sticky docs sub-header: breadcrumb + search + version. Shown on every page. */}
      <DocsSubHeader/>
      <main className="site-main">
        <Outlet/>
      </main>
      <footer className="site-footer">
        <div className="footer-main">
          <div className="footer-brand">
            <img
              className="brand-logo"
              src={theme === 'dark' ? '/assets/brand/logo-white.svg' : '/assets/brand/logo-forrest.svg'}
              alt="NordicScreen"
            />
            <p>Documentation, APIs and tooling to build on Q-Play, Q-Cal and the NordicScreen platform.</p>
          </div>
          <div className="footer-cols">
            <div className="footer-col">
              <h4>Product</h4>
              <a href="https://app.q-play.io/" target="_blank" rel="noreferrer">Q-Play</a>
              <a href="https://nordicscreen.com/apps-and-integrations" target="_blank" rel="noreferrer">Apps &amp; Integrations</a>
              <a href="https://nordicscreen.com/supported-platforms" target="_blank" rel="noreferrer">Supported Platforms</a>
              <a href="https://nordicscreen.com/build-your-own-app" target="_blank" rel="noreferrer">Build your own app</a>
            </div>
            <div className="footer-col">
              <h4>Developer</h4>
              <Link to="/docs">Documentation</Link>
              <a href="https://status.nordicscreen.com/" target="_blank" rel="noreferrer">Status &amp; Operations</a>
              <a href="https://github.com/NordicScreen" target="_blank" rel="noreferrer">GitHub</a>
              <a href="https://help.nordicscreen.com/portal/en/home" target="_blank" rel="noreferrer">Support</a>
            </div>
            <div className="footer-col">
              <h4>Company</h4>
              <a href="https://nordicscreen.com/about" target="_blank" rel="noreferrer">About</a>
              <a href="https://nordicscreen.com/contact" target="_blank" rel="noreferrer">Contact</a>
              <a href="https://nordicscreen.com/legal" target="_blank" rel="noreferrer">Legal</a>
              <a href="https://nordicscreen.com/legal/cookie-and-privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <span>© {new Date().getFullYear()} NordicScreen · v{__APP_VERSION__}</span>
          <a href="https://nordicscreen.com" target="_blank" rel="noreferrer">nordicscreen.com</a>
        </div>
      </footer>
    </div>
  )
}
